import { useState, useMemo } from 'react';
import { Loader2, BarChart3 } from 'lucide-react';
import { useGroups } from '@/hooks/useGroups';
import { useStatistics, useStatsPeriodLabels } from '@/hooks/useStatistics';
import { StatisticsFilters } from '@/components/statistics/StatisticsFilters';
import { KpiCards } from '@/components/statistics/KpiCards';
import { SpendingOverTimeChart } from '@/components/statistics/SpendingOverTimeChart';
import { CategoryBreakdownChart } from '@/components/statistics/CategoryBreakdownChart';
import { MemberContributionsChart } from '@/components/statistics/MemberContributionsChart';
import { BalanceOverTimeChart } from '@/components/statistics/BalanceOverTimeChart';
import { GroupComparisonChart } from '@/components/statistics/GroupComparisonChart';
import { TopExpensesTable } from '@/components/statistics/TopExpensesTable';
import type { TimeRange } from '@/api/statistics';
import { useTranslation } from 'react-i18next';

export function StatisticsPage() {
  const { t } = useTranslation();
  const { data: groups } = useGroups();
  const [timeRange, setTimeRange] = useState<TimeRange>('3M');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();

  const { data: stats, isLoading, isError, error } = useStatistics(timeRange, selectedGroupId, periodOffset);
  const periodLabels = useStatsPeriodLabels(timeRange, periodOffset);

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    setPeriodOffset(0);
  };

  const groupOptions = useMemo(
    () =>
      groups?.map(g => ({
        id: g.id,
        name: g.name,
        color: g.color ?? '#6366F1',
      })),
    [groups]
  );

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">{t('statistics:title')}</h1>
        <p className="mt-1 text-text-secondary">{t('web:statisticsPage.analyzeYourSpendingPatternsAndBalances')}</p>
      </div>

      {/* Filters */}
      <StatisticsFilters
        timeRange={timeRange}
        onTimeRangeChange={handleTimeRangeChange}
        periodLabels={periodLabels}
        periodOffset={periodOffset}
        onPeriodOffsetChange={setPeriodOffset}
        groups={groupOptions}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {t('web:statisticsPage.failedToLoadStatistics', { message: error instanceof Error ? error.message : t('web:common.unknownError') })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      )}

      {/* Empty state (no groups) */}
      {!isLoading && (!groups || groups.length === 0) && (
        <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
            <BarChart3 className="h-6 w-6 text-text-quaternary" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-text-primary">{t('web:statisticsPage.noDataYet')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('web:statisticsPage.createAGroupAndAddSome')}
          </p>
        </div>
      )}

      {/* Statistics content */}
      {stats && (
        <div className="relative overflow-hidden rounded-xl min-h-[300px]">
          <div>
            {stats.kpi.expenseCount === 0 ? (
              <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
                  <BarChart3 className="h-6 w-6 text-text-quaternary" />
                </div>
                <h3 className="mt-4 text-sm font-medium text-text-primary">{t('web:common.notEnoughData')}</h3>
                <p className="mt-1 text-sm text-text-tertiary">
                  {t('web:common.noExpensesFoundForTheSelected')}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* KPI Cards */}
                <KpiCards data={stats.kpi} currency={stats.displayCurrency} />

                {/* Charts — 2-column grid */}
                <div className="grid gap-6 lg:grid-cols-2">
                  <SpendingOverTimeChart
                    data={stats.spendingOverTime}
                    currency={stats.displayCurrency}
                  />
                  <CategoryBreakdownChart
                    data={stats.categoryBreakdown}
                    currency={stats.displayCurrency}
                    totalSpent={stats.kpi.totalSpent}
                  />
                  <MemberContributionsChart
                    data={stats.memberContributions}
                    currency={stats.displayCurrency}
                  />
                  <BalanceOverTimeChart
                    data={stats.balanceOverTime}
                    currency={stats.displayCurrency}
                  />
                </div>

                {/* Group comparison — full width, only when "All Groups" */}
                {!selectedGroupId && (
                  <GroupComparisonChart
                    data={stats.groupComparison}
                    currency={stats.displayCurrency}
                  />
                )}

                {/* Top expenses table */}
                <TopExpensesTable
                  data={stats.topExpenses}
                  currency={stats.displayCurrency}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
